import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Popover,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { format, subDays, subWeeks, subMonths, startOfDay, endOfDay } from 'date-fns';

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
  label?: string;
}

interface DashboardDatePickerProps {
  value: DateRange;
  onChange: (dateRange: DateRange) => void;
  onApply?: (dateRange: DateRange) => void;
}

const DashboardDatePicker: React.FC<DashboardDatePickerProps> = ({
  value,
  onChange,
  onApply,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const predefinedRanges = [
    {
      label: 'Today',
      startDate: startOfDay(new Date()),
      endDate: endOfDay(new Date()),
    },
    {
      label: 'Yesterday',
      startDate: startOfDay(subDays(new Date(), 1)),
      endDate: endOfDay(subDays(new Date(), 1)),
    },
    {
      label: 'Last 7 Days',
      startDate: startOfDay(subDays(new Date(), 6)),
      endDate: endOfDay(new Date()),
    },
    {
      label: 'Last 30 Days',
      startDate: startOfDay(subDays(new Date(), 29)),
      endDate: endOfDay(new Date()),
    },
    {
      label: 'This Month',
      startDate: startOfDay(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
      endDate: endOfDay(new Date()),
    },
    {
      label: 'Last Month',
      startDate: startOfDay(subMonths(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 1)),
      endDate: endOfDay(new Date(new Date().getFullYear(), new Date().getMonth(), 0)),
    },
    {
      label: 'Last 3 Months',
      startDate: startOfDay(subMonths(new Date(), 3)),
      endDate: endOfDay(new Date()),
    },
    {
      label: 'All Time',
      startDate: null,
      endDate: null,
    },
  ];

  const handlePredefinedRangeClick = (range: DateRange) => {
    const newRange = { ...range };
    onChange(newRange);
    if (onApply) {
      onApply(newRange);
    }
    handleClose();
  };

  const handleCustomDateChange = (field: 'startDate' | 'endDate', dateString: string) => {
    const date = dateString ? new Date(dateString) : null;
    const newRange = {
      ...value,
      [field]: date,
      label: 'Custom Range',
    };
    onChange(newRange);
  };

  const handleApplyCustom = () => {
    if (onApply) {
      onApply(value);
    }
    handleClose();
  };

  const formatDateRange = (range: DateRange): string => {
    if (range.label && range.label !== 'Custom Range') {
      return range.label;
    }
    
    if (!range.startDate && !range.endDate) {
      return 'All Time';
    }
    
    if (range.startDate && range.endDate) {
      return `${format(range.startDate, 'MMM dd, yyyy')} - ${format(range.endDate, 'MMM dd, yyyy')}`;
    }
    
    if (range.startDate) {
      return `From ${format(range.startDate, 'MMM dd, yyyy')}`;
    }
    
    if (range.endDate) {
      return `Until ${format(range.endDate, 'MMM dd, yyyy')}`;
    }
    
    return 'Select Date Range';
  };

  return (
    <Box>
      <Button
        variant="outlined"
        startIcon={<DateRangeIcon />}
        onClick={handleClick}
        sx={{
          minWidth: 200,
          justifyContent: 'flex-start',
          textTransform: 'none',
        }}
      >
        {formatDateRange(value)}
      </Button>
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: { minWidth: 400, mt: 1 }
        }}
      >
        <Card elevation={0}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon fontSize="small" />
              Select Date Range
            </Typography>
            
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Quick Ranges
            </Typography>
            
            <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
              {predefinedRanges.map((range) => (
                <Chip
                  key={range.label}
                  label={range.label}
                  onClick={() => handlePredefinedRangeClick(range)}
                  variant={value.label === range.label ? 'filled' : 'outlined'}
                  color={value.label === range.label ? 'primary' : 'default'}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                />
              ))}
            </Stack>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Custom Range
            </Typography>
            
            <Stack spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                value={value.startDate ? format(value.startDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
              
              <TextField
                label="End Date"
                type="date"
                value={value.endDate ? format(value.endDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
              
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="outlined" onClick={handleClose} size="small">
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleApplyCustom} 
                  size="small"
                  disabled={!value.startDate && !value.endDate}
                >
                  Apply
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Popover>
    </Box>
  );
};

export default DashboardDatePicker;